import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FeatureCard from "./FeatureCard";
import StageMode_cafe from "../../components/assets/StageMode_cafe.png";
import GroupSession from "../../components/assets/GroupSession.png";
import OnetoOneSession from "../../components/assets/OnetoOneSession.png";
import StageLink from "../../components/assets/StageLink.png";
import suit_distinction from "../../components/assets/suit_distinction.png";
import onDemandLive from "../../components/assets/whyTaasNet/OnDemandLive.png";
import createCards from "../../components/assets/whyTaasNet/createCards.png";
import { StageLinkLogo } from "../../components/Branding/StageLinkLogo";
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
  border: "rgba(255,255,255,0.12)",
};

const features = [
  {
    title: "Live Your Passion, Every Suit.",
    description:
      "Heart for Live Entertainment, Spade for Learning, Clover for Events, and Diamond for Commerce – Your All-in-One Live Connection.",
    image: suit_distinction,
  },
  { title: "Live StageLink Events", description: "Join talents in real time and be part of the action.", image: StageLink },
  { title: "One-on-One Sessions", description: "Book personalized experiences with your favorite talents.", image: OnetoOneSession },
  { title: "Group Sessions", description: "Interact in small, curated groups with shared interests.", image: GroupSession },
  { title: "StageMode Sessions", description: "Join large-audience sessions hosted by top creators and mentors.", image: StageMode_cafe },
  { title: "Create TaasCards for RealTime Services", description: "Manage your service offerings and availability live.", image: createCards },
  { title: "Free Drop-in On-Going liveSession", description: "Experience live sessions anytime, drop in free.", image: onDemandLive },
];

const AUTO_SCROLL_MS = 3500;

const WhyChooseTaaSNet: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Duplicate for looping rail
  const rail = useMemo(() => [...features, ...features], []);

  const [paused, setPaused] = useState(false);

  // Auto-scroll like Netflix rails; loop seamlessly
useEffect(() => {
  const el = scrollRef.current;
  if (!el) return;

  let timeoutId: number | null = null;

  const tick = () => {
    const node = scrollRef.current;
    if (!node) return;

    const half = node.scrollWidth / 2;
    const step = Math.max(240, Math.floor(node.clientWidth * 0.9));

    if (!paused) {
      node.scrollBy({ left: step, behavior: "smooth" });
    }

    window.setTimeout(() => {
      if (node.scrollLeft >= half - 2) {
        node.scrollTo({ left: node.scrollLeft - half });
      }
    }, 420);

    timeoutId = window.setTimeout(tick, AUTO_SCROLL_MS) as unknown as number;
  };

  timeoutId = window.setTimeout(tick, AUTO_SCROLL_MS) as unknown as number;

  return () => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  };
}, [paused]);

  const handleNext = () => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(240, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: step, behavior: "smooth" });
  };

  const handlePrev = () => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(240, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: -step, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        position: "relative",
        backgroundColor: COLORS.bg,
        // subtle cinematic glow
        backgroundImage:
          "radial-gradient(900px 500px at 10% 0%, rgba(229,9,20,0.10), transparent 60%), radial-gradient(900px 500px at 90% 10%, rgba(255,255,255,0.06), transparent 60%)",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      whiteSpace: "nowrap",         // keep in one line
    }}
  >
    <Typography
      variant={smDown ? "h5" : "h4"}
      component="span"
      sx={{
        color: COLORS.text,
        fontWeight: 900,
        letterSpacing: "-0.02em",
        textShadow: "0 1px 0 rgba(0,0,0,0.4)",
        lineHeight: 1,               // align better with logo
      }}
    >
      Why 
    </Typography>

    {/* Wrap the logo so it behaves inline */}
    <Box sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
      <StageLinkLogo />
    </Box>
  </Box>

  <Box
    sx={{
      mt: 1,
      height: 3,
      width: 72,
      mx: "auto",
      borderRadius: 999,
      background: COLORS.red,
      boxShadow: "0 8px 20px rgba(229,9,20,0.35)",
    }}
  />
</Box>


        {/* Rail container */}
        <Box
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          sx={{
            position: "relative",
          }}
        >
          {/* Edge masks for a Netflix fade effect */}
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              inset: 0,
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
              maskImage:
                "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
            }}
          />

          {/* Arrow controls (desktop) */}
          {mdUp && (
            <>
              <IconButton
                onClick={handlePrev}
                aria-label="previous"
                sx={{
                  position: "absolute",
                  left: -4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                aria-label="next"
                sx={{
                  position: "absolute",
                  right: -4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          {/* Scrollable rail */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollBehavior: "smooth",
              px: { xs: 0.5, md: 1 },
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {rail.map((feature, idx) => (
              <Box
                key={`${feature.title}-${idx}`}
                sx={{
                  flex: "0 0 auto",
                  minWidth: {
                    xs: "80%",
                    sm: "clamp(260px, 45vw, 360px)",
                    md: "clamp(260px, 30vw, 340px)",
                  },
                  scrollSnapAlign: "start",
                  // subtle depth without assuming FeatureCard's bg
                  filter: "drop-shadow(0 14px 28px rgba(0,0,0,0.45))",
                }}
              >
                <FeatureCard {...feature} />
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseTaaSNet;
